<template>
  <v-navigation-drawer
    v-model="show"
    app
    floating
    :class="{'block-loading' : blockLoading}"
  >
    <div class="mr-auto pt-5 pl-3 d-flex">
      <v-btn
        v-if="$vuetify.breakpoint.mobile"
        icon
        class="mr-3"
        @click="show = false"
      >
        <v-icon>{{ backIcon }}</v-icon>
      </v-btn>
      <div :style="{'max-width': logoMaxWidth + 'px'}">
        <img
          :src="logo"
          style="max-height:80px;max-width:100%"
          class="hover-pointer"
          @click="logoClick"
        >
      </div>
    </div>

    <div class="mt-7">
      <div
        v-if="showUpgrade"
        v-ripple
        class="border-thick bordered rounded-lg pa-2 mx-3 primary--text hover-pointer"
        :style="{borderColor: $vuetify.theme.themes.light.primary}"
        @click="upgrade"
      >
        <div class="d-flex justify-space-between">
          <div>
            <v-icon color="primary">
              {{ crownIcon }}
            </v-icon>
            Upgrade to pro
          </div>
          <v-icon
            v-if="showUpgradeArrow"
            color="primary"
          >
            {{ arrowRightIcon }}
          </v-icon>
        </div>
      </div>

      <v-alert
        v-if="runningAs"
        dense
        color="tan"
        class="caption mx-3 rounded-lg overflow-hidden"
      >
        <template v-slot:prepend>
          <v-icon
            small
            class="mr-1"
          >
            {{ runasIcon }}
          </v-icon>
        </template>
        <div class="text-truncate">
          {{ runningAs }}
        </div>
        <template v-slot:close>
          <v-btn
            x-small
            icon
            @click="cancelRunas"
          >
            <v-icon small>
              {{ closeIcon }}
            </v-icon>
          </v-btn>
        </template>
      </v-alert>

      <v-list
        class="px-2"
        dense
      >
        <template v-for="item in mainItemsToRender">
          <v-list-item
            v-if="!item.children"
            :key="item.title"
            link
            :to="item.route"
            active-class="nav-active"
            :ripple="false"
            v-on="item.click ? { click: item.click } : {}"
          >
            <v-list-item-icon>
              <v-icon class="thick">
                {{ item.icon }}
              </v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-group
            v-else
            :key="item.route"
            :prepend-icon="item.icon"
            no-action
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
            </template>
            <v-list-item
              v-for="child in item.children"
              :key="child.title"
              link
              :to="child.route"
              :disabled="child.disabled"
            >
              <v-list-item-content>
                <v-list-item-title>{{ child.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>
    </div>

    <template v-slot:append>
      <v-list
        class="mt-5 px-2"
        dense
      >
        <v-list-item
          v-for="item in footerItemsToRender"
          :key="item.title"
          :link="item.route !== null"
          :to="item.route"
          v-on="item.click ? { click: item.click } : {}"
        >
          <v-list-item-icon>
            <v-icon class="thick">
              {{ item.icon }}
            </v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { APP_EVENTS, DRAWERS, ROUTES } from '../../utils/constants';
import runas from '../../utils/mixins/runas';

import {
  mdiGrid,
  mdiFileMultipleOutline,
  mdiCreditCardOutline,
  mdiFolderOutline,
  mdiHelpCircleOutline,
  mdiLogout,
  mdiHeartOutline,
  mdiAccountKeyOutline,
  mdiAccountOffOutline,
  mdiClose,
  mdiGiftOutline,
  mdiPrinterOutline,
  mdiKeyboardBackspace,
  mdiArrowRight,
  mdiCrownOutline,
  mdiAccountSupervisorCircle,
  mdiHeartPlusOutline
} from '@mdi/js';

export default {
  name: 'Nav',

  mixins: [runas],

  data () {
    return {
      show: false,
      runasIcon: mdiAccountKeyOutline,
      closeIcon: mdiClose,
      backIcon: mdiKeyboardBackspace,
      arrowRightIcon: mdiArrowRight,
      crownIcon: mdiCrownOutline,
      beaconId: 'b1774456-e1e1-4335-bf72-d1c8d069f462'
    };
  },

  computed: {
    logo () {
      return this.$store.state.config.logoUrlDark;
    },

    logoMaxWidth () {
      if (this.$vuetify.breakpoint.mobile) return '110';
      else if (!this.$store.getters['user/isMCS']) return '230';
      return 110;
    },

    runningAs () {
      return this.$store.state.config.runas;
    },

    showUpgrade () {
      if (!this.$store.getters['config/enableSubscriptions']) return false;
      if (!this.$store.state.user.plan.isSubscriber) return true;
      if (this.$store.state.user.plan.isSubscriber && this.$store.state.user.plan.subscriptionIsCanceled) return true;
      return false;
    },

    showUpgradeArrow () {
      return this.$store.state.config.route.path === ROUTES.SUBSCRIPTION.PATH && !this.$vuetify.breakpoint.mobile;
    },

    blockLoading () {
      return this.$store.state.config.blockUILoading;
    },

    mainItems () {
      var items = [{
        name: 'Dashboard',
        icon: mdiGrid,
        route: ROUTES.DASHBOARD.PATH
      }, {
        name: 'My Projects',
        icon: mdiFileMultipleOutline,
        route: ROUTES.PROJECTS.PATH
      }, {
        name: 'Printing',
        icon: mdiPrinterOutline,
        route: ROUTES.PRINT.PATH
      }, {
        name: 'Nonprofit Details',
        icon: mdiHeartPlusOutline,
        click: this.goNonProfit,
        render: this.$store.getters['user/isMissionPrint']
      }, {
        name: 'My Orders',
        icon: mdiGiftOutline,
        route: ROUTES.ORDER_HISTORY.PATH,
        render: this.$store.state.user.hasOrdered
      }, {
        name: 'Billing',
        icon: mdiCreditCardOutline,
        render: this.$store.getters['config/enableSubscriptions'] || this.$store.state.user.hasOrdered,
        children: [{
          name: 'Subscription',
          route: ROUTES.SUBSCRIPTION.PATH,
          render: this.$store.getters['config/enableSubscriptions']
        }, {
          name: 'Payment Methods',
          route: ROUTES.CARDS.PATH,
          render: this.$store.state.user.hasOrdered
        }]
      }, {
        name: 'Give',
        icon: mdiHeartOutline,
        render: this.$store.state.user.profile.isGiveAdmin,
        click: this.give
      }, {
        name: 'Contributor',
        icon: mdiFolderOutline,
        render: this.$store.getters['user/isAdmin'] || this.$store.getters['user/isDesigner'] || this.$store.getters['user/isAssetContributor'] || this.$store.getters['user/isImageContributor'],
        children: [{
          name: 'Design Projects',
          route: ROUTES.CONTRIBUTOR_DESIGN_PROJECTS.PATH,
          render: this.$store.getters['user/isAdmin'] || this.$store.getters['user/isDesigner']
        }, {
          name: 'Asset Projects',
          route: ROUTES.CONTRIBUTOR_ASSET_PROJECTS.PATH,
          render: this.$store.getters['user/isAdmin'] || this.$store.getters['user/isAssetContributor']
        },
        {
          name: 'Image Projects',
          route: ROUTES.CONTRIBUTOR_IMAGE_PROJECTS.PATH,
          render: this.$store.getters['user/isAdmin'] || this.$store.getters['user/isImageContributor']
        }, {
          name: 'Payouts',
          route: ROUTES.CONTRIBUTOR_PAYOUTS.PATH
        }]
      }, {
        name: 'Admin',
        icon: mdiAccountSupervisorCircle,
        render: this.$store.getters['user/isMasterAccountAdmin'],
        children: [{
          name: 'Banking',
          route: { name: ROUTES.ADMIN_BANKING.NAME },
          disabled: this.$store.getters['user/isMCS']
        }, {
          name: 'App Setup',
          route: { name: ROUTES.ADMIN_APP_SETUP.NAME }
        }, {
          name: 'Product Setup',
          route: { name: ROUTES.ADMIN_PRODUCT_SETUP.NAME }
        }, {
          name: 'Master Accounts',
          route: { name: ROUTES.ADMIN_MASTER_ACCOUNTS.NAME },
          render: this.$store.getters['user/isAdmin']
        }, {
          name: 'Tax Exemptions',
          route: { name: ROUTES.ADMIN_EXEMPTION_CERTS.NAME },
          render: this.$store.getters['user/isMasterAccountAdmin'] && this.$store.getters['user/isMissionPrint']
        }, {
          name: 'Reports',
          route: { name: ROUTES.ADMIN_REPORTS_SALES.NAME },
          disabled: this.$store.getters['user/isMCS']
        }]
      }];
      return items;
    },

    mainItemsToRender () {
      var items = [];
      this.mainItems.forEach(parent => {
        if (parent.render !== false) {
          var item = parent;
          if (item.children) {
            var children = [];
            item.children.forEach(child => {
              if (child.render !== false) {
                children.push(child);
              }
            });
            item.children = children;
          }
          items.push(item);
        }
      });
      return items;
    },

    footerItems () {
      var items = [{
        name: 'Help Center',
        icon: mdiHelpCircleOutline,
        click: this.help,
        render: this.$store.state.config.helpscoutReady
      }, {
        name: !this.runningAs ? 'Run As' : 'Cancel Run As',
        icon: !this.runningAs ? mdiAccountKeyOutline : mdiAccountOffOutline,
        click: !this.runningAs ? () => { this.runas(null); } : this.cancelRunas,
        render: this.$store.getters['user/isAdmin']
      }, {
        name: 'Logout',
        icon: mdiLogout,
        click: this.logout
      }];
      return items;
    },

    footerItemsToRender () {
      return this.footerItems.filter(x => x.render !== false);
    }
  },

  mounted () {
    this.$eventBus.$on(APP_EVENTS.SET_NAV_VISIBILITY, (val) => {
      this.show = val;
    });
    this.show = !this.$vuetify.breakpoint.mobile;
  },

  methods: {
    logoClick () {
      if (this.$portalMode()) {
        if (this.$store.state.user.masterAccount.homePageUrl) {
          window.location.href = this.$store.state.user.masterAccount.homePageUrl;
        } else {
          this.$router.push(ROUTES.DASHBOARD.PATH);
        }
      } else {
        window.location = this.$store.state.config.webUrl;
      }
    },

    help () {
      this.$eventBus.$emit(APP_EVENTS.OPEN_HELPSCOUT);
    },

    give () {
      window.location = process.env.VUE_APP_WEB_URL + 'give-admin';
    },

    goNonProfit () {
      this.$store.commit('drawers/OPEN_DRAWER', DRAWERS.NON_PROFIT_DRAWER);
    },

    cancelRunas () {
      this.$store.commit('config/SET_RUNNING_AS', null);
      this.$router.go(ROUTES.DASHBOARD.PATH);
    },

    upgrade () {
      this.$router.push(ROUTES.SUBSCRIPTION.PATH);
    },

    logout () {
      window.location = this.$store.state.config.webUrl + 'account/logout';
    }
  }
};
</script>

<style>
.nav-active .v-list-item__title {
  font-weight:700 !important
}
.v-list-item::before {
  border-radius:7px !important;
}
.nav-active::before, .v-list-item:hover::before, .v-list-item:focus::before {
  background-color:#aaa5ae;
}
.v-list-item__icon:first-child {
  margin-right:18px !important;
}
.v-alert.overflow-hidden .v-alert__content {
  overflow:hidden;
}
</style>
